import React from 'react';

const emotions = [
  {
    key: 'love',
    emoji: '❤️',
    name: 'Love',
    definition:
      'An emotion characterized by strong feelings of affection with or without it being directed at any particular persons but a state of being loving, compassionate, towards things animate or inanimate,  and can be associated with the term Agape love.',
    accent: '#e63946',
  },
  {
    key: 'gratitude',
    emoji: '🙏',
    name: 'Gratitude',
    definition:
      'A feeling of thankfulness or appreciation for someone or something in your life, often accompanied by feelings of warmth, kindness, and a desire to reciprocate.',
    accent: '#f4a261',
  },
  {
    key: 'joy',
    emoji: '😄',
    name: 'Joy',
    definition:
      'A state of being that allows one to experience feelings of intense, long-lasting happiness and contentment of life.',
    accent: '#2a9d8f',
  },
  {
    key: 'pride',
    emoji: '🏆',
    name: 'Pride',
    definition:
      "A feeling of satisfaction and accomplishment in oneself, one's abilities, or the accomplishments of others. Pride can be positive, motivating, and empowering, and it can also lead to arrogance and overconfidence if not balanced with humility and self-awareness",
    accent: '#6c63ff',
  },
  {
    key: 'surprise',
    emoji: '😲',
    name: 'Surprise',
    definition:
      'A sudden and unexpected feeling caused by something unexpected or unusual happening and it can be positive or negative.',
    accent: '#48cae4',
  },
  {
    key: 'sadness',
    emoji: '😢',
    name: 'Sadness',
    definition:
      'A natural emotion characterized by sorrow, disappointment, and despair. It can be activated by loss, rejection, failure, separation from loved ones, or unmet emotional needs.',
    accent: '#457b9d',
  },
  {
    key: 'anger',
    emoji: '😠',
    name: 'Anger',
    definition:
      'A normal emotional response to a person, situation or oneself when you believe you have been treated unfairly or has otherwise been hurt or harmed. Anger emerges when you feel threatened emotionally, physically, financially, or in other ways.',
    accent: '#c1121f',
  },
  {
    key: 'disgust',
    emoji: '🤢',
    name: 'Disgust',
    definition:
      'A strong feeling of revulsion or aversion towards something offensive. It can be activated  by a variety of things, including: Smells, Tastes, Sounds, Sight, Touch, Actions or appearances of people, and Ideas',
    accent: '#606c38',
  },
  {
    key: 'fear',
    emoji: '😨',
    name: 'Fear',
    definition:
      'An emotion that arises when an individual perceives a threat or danger, whether real or imagined, causing physiological and psychological changes in the body, often leading to a "fight-flight-freeze" response aimed at avoiding or confronting the perceived threat; it serves as a protective mechanism to alert us to potential harm.',
    accent: '#7b2d8b',
  },
];

const guideSections = [
  {
    id: 'primary-emotions',
    title: 'What is the emotion you are having? (Primary Emotions)',
    paragraphs: [
      'Scientists have found that there are certain core emotions that everyone feels and are grouped generally as primary emotions. These emotions are generally seen as positive (happy) or negative (sad) emotions. We list them here as high or low vibrational experiences. When these core emotions arise in us, they are felt simultaneously in the body and generally only last for about 90 seconds. These primary emotions are like the building blocks of our emotional world.',
    ],
    lists: [
      { label: 'High Vibration', items: ['Love', 'Joy', 'Gratitude', 'Surprise', 'Pride'] },
      { label: 'Low Vibration', items: ['Anger', 'Sadness', 'Fear', 'Disgust'] },
    ],
  },
  {
    id: 'intensity',
    title: 'How Strong Is It? (Intensity)',
    paragraphs: [
      'Intensity refers to how strongly an emotion is experienced ranging from mild to powerful. For example: mild is a slight annoyance when a store is out of something; medium is disappointment after studying hard and still getting a bad grade; strong is intense joy from great news or overwhelming sadness from a major loss. Stronger emotions often lead to quicker or more intense reactions. When a sad or anxious feeling persists it may become an underlying mood like melancholy or depression while persistent positive feelings can reflect overall life satisfaction and inner peace.',
    ],
  },
  {
    id: 'body-sensations',
    title: 'What Does It Feel Like in My Body? (Body Sensations)',
    paragraphs: [
      "Body sensations change when we have emotions there is always a body experience when our emotions are activated. We might notice our heart beating faster when we are scared, rapid breathing and sweating when anxious, or feel warm and light when happy. These physical changes help us know what we're feeling.",
    ],
  },
  {
    id: 'feelings',
    title: 'What Am I Actually Feeling? (Feelings)',
    paragraphs: [
      'Feelings are the detailed, specific ways we experience our core emotions. Anger might show up as aggravation or frustration; sadness can feel like disappointment or loneliness; joy can be contentment or excitement. Developing a vocabulary for these specific feelings is crucial  recognizing "frustration" instead of just "anger" helps us address the root issue. Unlike brief primary emotions, feelings can last longer and we can experience multiple feelings simultaneously, like shame, guilt, or hope.',
    ],
  },
  {
    id: 'what-happened',
    title: 'How Am I Being With This Experience? (What Happened)',
    paragraphs: [
      'Being with the experience is how our response generates a particular behavioral outcome. With a negative experience you may become avoidant, withdraw, or engage in self-blame. The experience is everything that happens during an emotional moment what started the feeling, what we felt inside, how we acted, and what happened after. For example, forgetting your lines during a presentation, feeling embarrassed (internal sensation), making a self-deprecating joke (behavior), then receiving reassuring feedback (aftermath). This entire sequence not just the embarrassment  is the complete emotional experience.',
    ],
  },
  {
    id: 'perceptions',
    title: 'What Are My Judgments? (Perceptions)',
    paragraphs: [
      'Our perceptions are how we understand and judge events, including how we judge ourselves. Everyone interprets things differently based on their past, beliefs, and values. For example, forgetting your lines during a presentation: one person might think "I messed up, I\'m incompetent" (fear of judgment); another, "I recovered well, everyone makes mistakes" (resilience); another, "The message is still getting across" (positive focus). How we judge ourselves in emotional situations is as important as how we judge others, and these perceptions shape our sense of well-being.',
    ],
  },
  {
    id: 'with-whom-or-what',
    title: 'Who or What Activated This Feeling? (With Whom or What Situation)',
    paragraphs: [
      'Understanding why you feel a certain way involves pinpointing what "activated" that feeling. This could be a person their words, actions, or even their mere presence. It might be an event, such as an unexpected bill causing stress or achieving a goal bringing pride. Places can also evoke feelings, like nostalgia from a childhood home or anxiety in a crowded mall. Even your own thoughts  dwelling on past mistakes or having optimistic expectations can drive strong emotions. Identifying the "who or what" leads to greater self-awareness and the development of valuable coping skills.',
    ],
  },
  {
    id: 'what-i-need',
    title: 'What Do I Need? (What I Need)',
    paragraphs: [
      "Thinking about what you need when you're feeling something is really important. Sometimes we ignore our feelings or let others tell us how we should feel. Taking a moment to figure out what you really need at that moment helps you own what is happening and supports your ability to set healthy boundaries.",
    ],
  },
  {
    id: 'do-now',
    title: 'What Can I Do Now? (Do Now)',
    paragraphs: [
      'Asking yourself this question helps you handle your emotions better. After something emotional happens, it\'s important to stop, calm down, and think about how you want to respond — instead of reacting without thinking. If someone gives you criticism, ask "What can I do to understand this and stay calm?" If you\'re nervous about a presentation, ask "What can I do to feel less anxious and more confident?" If you just received exciting news, stop and take it all in. Whether emotions are strong or mild, calming your body and reflecting on why you feel that way helps you use your inner knowing to respond.',
    ],
  },
];

function SectionHeading({ id, title }) {
  return (
    <h4 className="emotion-guide-heading" id={id}>
      <a href={`#${id}`} className="emotion-guide-anchor" aria-label={`Link to ${title}`}>
        {title}
      </a>
    </h4>
  );
}

export default function EmotionDefinition() {
  React.useEffect(() => {
    document.title = 'Emotion Definitions';
    document.body.style.backgroundColor = '#F2F2F2';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <div className="container-fluid px-0">
      <div className="container-fluid py-3 mx-0" style={{ backgroundColor: '#33b2ea' }}>
        <h2 className="text-white">Emotion Definitions</h2>
      </div>
      <div
        className="container-fluid"
        style={{ maxWidth: 860, margin: '0 auto', padding: '2rem 1rem 4rem' }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {emotions.map((e) => (
            <div
              key={e.key}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '1.5rem 1.25rem',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                borderTop: `4px solid ${e.accent}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 32 }}>{e.emoji}</span>
                <span style={{ fontSize: 19, fontWeight: 700, color: e.accent }}>{e.name}</span>
              </div>
              <p style={{ fontSize: 15, lineHeight: 1.65, color: '#444' }}>{e.definition}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .emotion-guide {
          max-width: 860px;
          margin: 0 auto;
          padding: 1rem 1.25rem 4rem;
        }
        .emotion-guide-title {
          font-size: 1.9rem;
          font-weight: 700;
          color: #123868;
          margin-bottom: 1.25rem;
        }
        .emotion-guide-toc {
          background: #fff;
          border-radius: 16px;
          padding: 1.25rem 1.5rem;
          box-shadow: 0 2px 12px rgba(0,0,0,0.07);
          margin-bottom: 2rem;
        }
        .emotion-guide-toc h5 {
          font-weight: 700;
          color: #123868;
          margin-bottom: 0.75rem;
        }
        .emotion-guide-toc ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 0.5rem 1rem;
        }
        .emotion-guide-toc a {
          color: #33b2ea;
          text-decoration: none;
          font-size: 0.95rem;
        }
        .emotion-guide-toc a:hover {
          text-decoration: underline;
        }
        .emotion-guide-section {
          background: #fff;
          border-radius: 16px;
          padding: 1.5rem 1.75rem;
          box-shadow: 0 2px 12px rgba(0,0,0,0.07);
          margin-bottom: 1.5rem;
          scroll-margin-top: 90px;
        }
        .emotion-guide-heading {
          font-size: 1.15rem;
          font-weight: 700;
          color: #123868;
          margin-bottom: 0.75rem;
          scroll-margin-top: 90px;
        }
        .emotion-guide-anchor {
          color: #459dfb;
          text-decoration: none;
          margin-right: 0.4rem;
          font-weight: 400;
        }
        .emotion-guide-heading:hover .emotion-guide-anchor {
          color: #33b2ea;
        }
        .emotion-guide-section p {
          font-size: 15px;
          line-height: 1.7;
          color: #444;
          margin-bottom: 0;
        }
        .emotion-guide-lists {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-top: 1rem;
        }
        .emotion-guide-list-card {
          border-radius: 12px;
          padding: 0.9rem 1.1rem;
          background: #f7fafc;
        }
        .emotion-guide-list-card strong {
          display: block;
          margin-bottom: 0.4rem;
          color: #123868;
        }
        .emotion-guide-list-card ul {
          margin: 0;
          padding-left: 1.1rem;
        }
        .emotion-guide-warning {
          background: #fff4e5;
          border: 1px solid #f4a261;
          border-radius: 16px;
          padding: 1.5rem 1.75rem;
          margin-bottom: 1.5rem;
          scroll-margin-top: 90px;
        }
        .emotion-guide-warning h4 {
          color: #b45309;
        }
        .emotion-guide-warning p {
          font-size: 15px;
          line-height: 1.7;
          color: #6b4226;
          margin-bottom: 0;
        }
        @media (max-width: 768px) {
          .emotion-guide {
            padding: 0.5rem 1rem 4rem;
          }
          .emotion-guide-title {
            font-size: 1.5rem;
          }
          .emotion-guide-toc ul {
            grid-template-columns: 1fr;
          }
          .emotion-guide-lists {
            grid-template-columns: 1fr;
          }
          .emotion-guide-section,
          .emotion-guide-warning,
          .emotion-guide-toc {
            padding: 1.1rem 1.25rem;
          }
        }
        @media (max-width: 480px) {
          .emotion-guide-title {
            font-size: 1.3rem;
          }
          .emotion-guide-heading {
            font-size: 1.05rem;
          }
        }
      `}</style>

      <div className="emotion-guide">
        <h2 className="emotion-guide-title">Understanding Our Emotions</h2>

        {guideSections.map((section) => (
          <section key={section.id} className="emotion-guide-section">
            <SectionHeading id={section.id} title={section.title} />
            {section.paragraphs.map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
            {section.lists && (
              <div className="emotion-guide-lists">
                {section.lists.map((list) => (
                  <div className="emotion-guide-list-card" key={list.label}>
                    <strong>{list.label}</strong>
                    <ul>
                      {list.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}

        <section id="important-warning" className="emotion-guide-warning">
          <SectionHeading id="important-warning-heading" title="⚠️ Important Warning" />
          <p>
            If you have a feeling that stays with you for a very long time, makes it hard to focus,
            or really disrupts your daily life, it's important to talk to a trusted adult or a
            professional. Also, if you experience panic attacks, feel very upset, or have thoughts
            of hurting yourself, please get help right away. There are people who care and want to
            help you.
          </p>
        </section>
      </div>

      <footer
        className="border border-bottom-0 border-start-0 fixed-bottom bg-white border-end-0 py-2"
        style={{ borderColor: '#32b1e8 !important', color: '#123868' }}
      >
        <div className="text-center">
          <span className="fw-bold">
            {new Date().getFullYear()} The Tea Group. All Rights Reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}
